import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import {
	createPersonEmploymentRecordRequest,
	updatePersonEmploymentRecordRequest,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/apis/useEmploymentRecords.ts';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { PersonDataContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/hooks/usePersonData.ts';
import { EmploymentRecordFormValues } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EmploymentRecordDrawer/types.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

const emptyToUndefined = (value: string): string | undefined =>
	value && value.trim() ? value : undefined;

const emptyToNull = (value: string): string | null => (value && value.trim() ? value : null);

export const useEmploymentRecordDrawer = (onClose: () => void) => {
	const tCapManage = useTranslationCapManage();
	const { selectedEntityId } = useContext(CapManageControllerContext);
	const { refetchEmploymentRecords } = useContext(PersonDataContext);

	const createEmploymentRecordMutation = useMutation({
		mutationFn: createPersonEmploymentRecordRequest,
	});
	const updateEmploymentRecordMutation = useMutation({
		mutationFn: updatePersonEmploymentRecordRequest,
	});
	const isRequestLoading =
		createEmploymentRecordMutation.isPending || updateEmploymentRecordMutation.isPending;

	const onFormSubmit = async (
		formValues: EmploymentRecordFormValues,
		employmentRecordId?: string
	) => {
		if (employmentRecordId) {
			updateEmploymentRecordMutation.mutate(
				{
					employmentRecordId,
					body: {
						startDate: emptyToNull(formValues.startDate),
						endDate: emptyToNull(formValues.endDate),
						jobTitle: emptyToNull(formValues.jobTitle),
						employeeReference: emptyToNull(formValues.employeeReference),
						isPrimaryForEmployee: formValues.isPrimaryForEmployee,
					},
				},
				{
					onSuccess: () => {
						void refetchEmploymentRecords();
						showToastV2({
							message: tCapManage(
								'toasts.success.employment_record_updated_successfully',
								{
									defaultValue: 'Employment record updated successfully',
								}
							),
							state: 'success',
						});
						onClose();
					},
					onError: () => {
						showToastV2({
							message: tCapManage(
								'toasts.errors.failed_save_employment_record_update',
								{
									defaultValue: 'Failed to save employment record update',
								}
							),
							state: 'error',
						});
					},
				}
			);
		} else {
			createEmploymentRecordMutation.mutate(
				{
					personId: selectedEntityId,
					body: {
						employeeId: selectedEntityId,
						employerId: formValues.employerId,
						startDate: emptyToUndefined(formValues.startDate),
						endDate: emptyToUndefined(formValues.endDate),
						jobTitle: emptyToUndefined(formValues.jobTitle),
						employeeReference: emptyToUndefined(formValues.employeeReference),
						isPrimaryForEmployee: formValues.isPrimaryForEmployee,
					},
				},
				{
					onSuccess: () => {
						void refetchEmploymentRecords();
						showToastV2({
							message: tCapManage(
								'toasts.success.employment_record_created_successfully',
								{
									defaultValue: 'Employment record created successfully',
								}
							),
							state: 'success',
						});
						onClose();
					},
					onError: () => {
						showToastV2({
							message: tCapManage('toasts.errors.failed_create_employment_record', {
								defaultValue: 'Failed to create employment record',
							}),
							state: 'error',
						});
					},
				}
			);
		}
	};

	return { onFormSubmit, isRequestLoading };
};
